/**
 * Вбудовані тури режиму навчання.
 * Ціль кроку — значення атрибута `data-tour` на елементі інтерфейсу (додають самі екрани).
 * Тексти коротко пояснюють дію і правило фірми, яке з нею пов’язане.
 * Тури модулів хвилі 2Б описано в папках модулів (програвач уроку, картка матеріалу, конструктор правила).
 */

import { RULE_BUILDER_TOUR } from '../automation/tour';
import { MATERIAL_CARD_TOUR } from '../content/tour';
import { LESSON_PLAYER_TOUR } from '../kb/learning/tour';
import { LEGAL_MATTER_TOUR } from '../legal/tour';
import { MGMT_TREE_TOUR } from '../mgmt/tour';
import { TIME_TRACKER_TOUR } from '../time/tour';
import { CONNECTORS_GOOGLE_TOUR } from '../connectors/tour';
import { MEETING_CARD_TOUR, MEETINGS_LIST_TOUR } from '../meetings/tour';
import { MIGRATION_WIZARD_TOUR } from '../migration/tour';
import { PEOPLE_OFFBOARDING_TOUR, PEOPLE_PROFILE_TOUR } from '../people/tour';
import { FINANCE_MATTER_TOUR } from '../finance/tour';
import { REPORTS_BUILDER_TOUR } from '../reports/tour';

export type TourPlacement = 'auto' | 'top' | 'bottom' | 'left' | 'right';

export type TourStep = {
  id: string;
  /** Значення атрибута data-tour цілі. */
  target: string;
  title: string;
  body: string;
  /** Бажаний бік картки відносно цілі; якщо місця немає — обирається інший. */
  placement?: TourPlacement;
};

export type TourId =
  | 'my-work'
  | 'project-list'
  | 'project-board'
  | 'task-card'
  | 'timer'
  | 'admin'
  | 'lesson-player'
  | 'content-material'
  | 'automation-rule-builder'
  | 'time-tracker'
  | 'legal-matter'
  | 'mgmt-tree'
  | 'meeting-card'
  | 'meetings-list'
  | 'people-profile'
  | 'people-offboarding'
  | 'connectors-google'
  | 'migration-wizard'
  | 'finance-matter'
  | 'reports-builder';

export type TourDefinition = {
  id: TourId;
  title: string;
  /** Що покаже тур (для меню й довідки). */
  summary: string;
  /** Підказка, коли елементів тура зараз немає на екрані. */
  missingHint: string;
  steps: TourStep[];
};

export const TOURS: Record<TourId, TourDefinition> = {
  'my-work': {
    id: 'my-work',
    title: 'Моя робота',
    summary: 'Стартовий екран дня: групи за строками, швидке створення задач, пошук і вхідні.',
    missingHint: 'Відкрийте розділ «Моя робота» — підказки прив’яжуться до його елементів.',
    steps: [
      {
        id: 'nav',
        target: 'nav-my-work',
        title: 'Моя робота — початок дня',
        body: 'Тут зібрано відкриті задачі, де ви виконавець. Починайте день звідси: прострочене, сьогоднішнє й найближче видно одразу.',
      },
      {
        id: 'sections',
        target: 'my-work-sections',
        title: 'Групи за строком',
        body: 'Задачі розкладено за строками: прострочені, сьогодні, цей тиждень, пізніше, без строку. Прострочену задачу перенесіть із причиною або завершіть із результатом — «червоні» задачі фіксуються в контрольний час.',
      },
      {
        id: 'add',
        target: 'my-work-add',
        title: 'Власне доручення',
        body: 'Додайте задачу, не виходячи з переліку. За правилами фірми в задачі один виконавець і обов’язковий строк — система нагадає, якщо чогось бракує.',
      },
      {
        id: 'new-task',
        target: 'topbar-new-task',
        title: 'Нова задача — клавіша Q',
        body: 'Форма нової задачі відкривається з будь-якого екрана. Вкажіть проєкт, виконавця й строк. Доручення колезі після 15:00 система запропонує поставити на наступний робочий день.',
      },
      {
        id: 'search',
        target: 'topbar-search',
        title: 'Пошук — клавіша /',
        body: 'Знаходьте задачі за назвою чи номером Т-…, проєкти й колег. Ctrl/Cmd+K відкриває палітру команд.',
      },
      {
        id: 'inbox',
        target: 'nav-inbox',
        title: 'Вхідні',
        body: 'Сповіщення про доручення, згадки й зміни у ваших задачах. Правило фірми: на 11:00 понеділка непереглянутих сповіщень не лишається.',
      },
    ],
  },

  'project-list': {
    id: 'project-list',
    title: 'Список проєкту',
    summary: 'Етапи процесу як групи, ліміти робіт, рядок задачі й дії з кількома задачами.',
    missingHint: 'Відкрийте проєкт у поданні «Список» — підказки прив’яжуться до його елементів.',
    steps: [
      {
        id: 'views',
        target: 'project-views',
        title: 'Подання й фільтри',
        body: 'Ті самі задачі можна переглянути списком, дошкою, календарем чи хронологією. Поруч — фільтр за назвою, перемикач «Усі / Мої» й показ завершених.',
      },
      {
        id: 'section',
        target: 'list-section',
        title: 'Етап процесу',
        body: 'Групи списку — це етапи процесу в порядку проходження. Лічильник 2/3 показує відкриті задачі проти ліміту робіт етапу: коли ліміт досягнуто, спершу завершіть роботу на етапі.',
      },
      {
        id: 'add',
        target: 'list-add',
        title: 'Задача на етапі',
        body: 'Нова задача одразу потрапить на цей етап. Один виконавець і строк обов’язкові — без них сервер задачу не прийме.',
      },
      {
        id: 'row',
        target: 'list-row',
        title: 'Рядок задачі',
        body: 'Номер Т-… на полях, назва, виконавець і строк; прострочений строк — червоним. Клік або Enter відкриває картку, ↑/↓ — сусідній рядок, права кнопка миші — меню дій.',
      },
      {
        id: 'bulk',
        target: 'list-bulk',
        title: 'Кілька задач одразу',
        body: 'Позначте кілька задач, щоб змінити етап, виконавця чи строк для всіх разом. Правила діють і тут: перенесення строку чи зміна виконавця — з причиною.',
      },
      {
        id: 'guide',
        target: 'project-guide',
        title: 'Довідка процесу',
        body: 'Часті питання й навчальні матеріали саме цього процесу. Звідси ж можна пройти навчання ще раз або потренуватися на навчальному проєкті з вигаданими задачами.',
      },
    ],
  },

  'project-board': {
    id: 'project-board',
    title: 'Дошка процесу',
    summary: 'Колонки-етапи, ліміти й нормативи, перетягування карток і створення задачі на етапі.',
    missingHint: 'Відкрийте проєкт у поданні «Дошка» — підказки прив’яжуться до колонок.',
    steps: [
      {
        id: 'views',
        target: 'project-views',
        title: 'Подання й фільтри',
        body: 'Дошка показує задачі за етапами процесу. Перемикайтеся між дошкою, списком, календарем і хронологією — дані ті самі; фільтр і «Мої» звужують показ.',
      },
      {
        id: 'column',
        target: 'board-column',
        title: 'Колонка — це етап',
        body: 'Перетягніть картку в сусідню колонку, щоб перевести задачу на наступний етап. Якщо етап має контрольні питання чи обов’язкові поля, система запитає їх у момент переходу.',
      },
      {
        id: 'limits',
        target: 'board-limits',
        title: 'Ліміт і норматив етапу',
        body: '2/3 — відкриті задачі проти ліміту робіт, «5 год» — норматив перебування задачі на етапі в робочих годинах. Коли ліміт досягнуто, межа колонки підсвічується.',
      },
      {
        id: 'add',
        target: 'board-add',
        title: 'Нова задача на етапі',
        body: 'Плюс у заголовку колонки створює задачу одразу на цьому етапі. Виконавець і строк обов’язкові.',
      },
      {
        id: 'scroll',
        target: 'board-scroll',
        title: 'Уся дошка',
        body: 'Гортайте дошку вбік тачпадом, колесом із Shift або пальцем на телефоні. Клік по картці відкриває задачу збоку — дошка лишається перед очима.',
      },
      {
        id: 'guide',
        target: 'project-guide',
        title: 'Довідка процесу',
        body: 'Часті питання й навчальні матеріали саме цього процесу. Звідси ж можна пройти навчання ще раз або потренуватися на навчальному проєкті з вигаданими задачами.',
      },
    ],
  },

  'task-card': {
    id: 'task-card',
    title: 'Картка задачі',
    summary: 'Реквізити й правила фірми, опис, підзадачі, таймер, коментарі й завершення з результатом.',
    missingHint: 'Відкрийте картку будь-якої задачі — підказки прив’яжуться до її розділів.',
    steps: [
      {
        id: 'title',
        target: 'task-title',
        title: 'Назва задачі',
        body: 'Клацніть на назву, щоб змінити її, — зміни зберігаються самі. Формулюйте дію й об’єкт: «Підготувати позовну заяву», а не «Позов».',
      },
      {
        id: 'fields',
        target: 'task-fields',
        title: 'Виконавець і строк',
        body: 'У задачі один відповідальний виконавець і обов’язковий строк. Перенесення строку чи зміна виконавця потребує причини — вона залишиться в історії змін.',
      },
      {
        id: 'description',
        target: 'task-description',
        title: 'Опис',
        body: 'Опишіть очікуваний результат, вихідні дані й посилання на документи. Номерів карток, IBAN і паролів в описі не залишайте — система попередить про такі дані.',
      },
      {
        id: 'subtasks',
        target: 'task-subtasks',
        title: 'Підзадачі',
        body: 'Розбийте велику роботу на кроки з власними виконавцями й строками. Кожна підзадача — окрема задача з тими самими правилами.',
      },
      {
        id: 'timer',
        target: 'task-timer',
        title: 'Облік часу',
        body: 'Натисніть «Почати», коли берете задачу в роботу, і «Зупинити», коли закінчили. Опис запису — за стандартом «об’єкт_дія в минулому часі».',
      },
      {
        id: 'comments',
        target: 'task-comments',
        title: 'Коментарі',
        body: 'Обговорюйте задачу тут, а не в месенджерах: згадка через @ надсилає колезі сповіщення, а історія змін показує, хто й що змінив.',
      },
      {
        id: 'complete',
        target: 'task-complete',
        title: 'Завершення з результатом',
        body: 'Коло біля номера завершує задачу. За правилами фірми потрібно коротко описати результат: що зроблено й де його знайти.',
      },
    ],
  },

  timer: {
    id: 'timer',
    title: 'Облік часу',
    summary: 'Таймер у картці задачі, запущений таймер у верхній панелі й стандарт опису записів.',
    missingHint: 'Відкрийте картку задачі або розділ «Облік часу» — підказки прив’яжуться до таймера й записів.',
    steps: [
      {
        id: 'start',
        target: 'task-timer',
        title: 'Таймер у картці задачі',
        body: 'Натисніть «Почати», коли берете задачу в роботу. Запис одразу прив’язується до задачі, а її назва стає об’єктом опису.',
      },
      {
        id: 'single',
        target: 'task-timer',
        title: 'Один таймер на працівника',
        body: 'Запуск нового таймера автоматично зупиняє попередній — перемикайтеся між задачами без подвійного обліку.',
      },
      {
        id: 'topbar',
        target: 'topbar-timer',
        title: 'Запущений таймер завжди на виду',
        body: 'Поточний таймер видно у верхній панелі на будь-якому екрані: звідси його можна зупинити й уточнити опис.',
      },
      {
        id: 'description',
        target: 'time-row',
        title: 'Опис запису',
        body: 'Стандарт — «об’єкт_дія в минулому часі», наприклад «Договір оренди_перевірив(ла) й надіслав(ла) правки». Дію пишіть як досягнення: «підготував(ла) заяву», а не «готую заяву».',
      },
      {
        id: 'quality',
        target: 'time-row',
        title: 'Перевірка якості',
        body: 'Система позначить порожній опис, неприв’язаний запис, перекриття записів і запис понад норму годин. Обід і святкування не трекаються.',
      },
    ],
  },

  admin: {
    id: 'admin',
    title: 'Адміністрування',
    summary: 'Розділи адміністрування, політика задач і правила роботи з доступами.',
    missingHint: 'Відкрийте розділ «Адміністрування» — він доступний власникові й адміністраторам.',
    steps: [
      {
        id: 'sections',
        target: 'admin-nav',
        title: 'Розділи адміністрування',
        body: 'Працівники, підрозділи, команди, політика задач, токени API, вебхуки й календар. Розділ бачать лише власник і адміністратори.',
      },
      {
        id: 'people',
        target: 'admin-nav',
        title: 'Працівники й структура',
        body: 'Запрошуйте працівників, призначайте ролі доступу, розподіляйте людей за підрозділами й командами — від цього залежать права й видимість задач.',
      },
      {
        id: 'policy',
        target: 'admin-nav',
        title: 'Політика задач',
        body: 'Тут вмикаються правила фірми: один виконавець, обов’язковий строк, задача в проєкті, причина перенесення строку й зміни виконавця, результат під час завершення.',
      },
      {
        id: 'content',
        target: 'admin-content',
        title: 'Зміни діють одразу',
        body: 'Правила перевіряє сервер — для вебзастосунку, мобільних клієнтів, інтеграцій і ШІ-агентів. Змінюйте їх виважено й повідомляйте команду.',
      },
      {
        id: 'integrations',
        target: 'admin-nav',
        title: 'Токени й вебхуки',
        body: 'Токени API й вебхуки — для зовнішніх систем. Видавайте токен із мінімальними правами й відкликайте його, щойно він не потрібен.',
      },
    ],
  },

  // Модулі хвилі 2Б.
  'lesson-player': { id: 'lesson-player', ...LESSON_PLAYER_TOUR },
  'content-material': { id: 'content-material', ...MATERIAL_CARD_TOUR },
  'automation-rule-builder': { id: 'automation-rule-builder', ...RULE_BUILDER_TOUR },

  // Модулі хвилі 2А (облік часу, юридичний і управлінський контури).
  'time-tracker': { id: 'time-tracker', ...TIME_TRACKER_TOUR },
  'legal-matter': { id: 'legal-matter', ...LEGAL_MATTER_TOUR },
  'mgmt-tree': { id: 'mgmt-tree', ...MGMT_TREE_TOUR },

  // Модулі хвилі 2В (зустрічі, персонал, інтеграції, перенесення даних).
  'meeting-card': { id: 'meeting-card', ...MEETING_CARD_TOUR },
  'meetings-list': { id: 'meetings-list', ...MEETINGS_LIST_TOUR },
  'people-profile': { id: 'people-profile', ...PEOPLE_PROFILE_TOUR },
  'people-offboarding': { id: 'people-offboarding', ...PEOPLE_OFFBOARDING_TOUR },
  'connectors-google': { id: 'connectors-google', ...CONNECTORS_GOOGLE_TOUR },
  'migration-wizard': { id: 'migration-wizard', ...MIGRATION_WIZARD_TOUR },

  // Модулі хвилі 2Г (фінанси робіт, звіти й аналітика).
  'finance-matter': { id: 'finance-matter', ...FINANCE_MATTER_TOUR },
  'reports-builder': { id: 'reports-builder', ...REPORTS_BUILDER_TOUR },
};

export function getTour(id: string): TourDefinition | undefined {
  return Object.prototype.hasOwnProperty.call(TOURS, id) ? TOURS[id as TourId] : undefined;
}

/**
 * Тур для поточного екрана: картка задачі (?task= або /tasks/:id) має пріоритет над сторінкою під нею.
 */
export function tourForLocation(pathname: string, search: string): TourId | null {
  const params = new URLSearchParams(search);
  if (params.has('task') || /^\/tasks\/[^/]+/.test(pathname)) return 'task-card';
  // Картка матеріалу контенту (?material=) — поверх будь-якої вкладки модуля «Контент».
  if (params.has('material') && /^\/content(\/|$)/.test(pathname)) return 'content-material';
  if (/^\/kb\/learning\/lessons\/[^/]+\/?$/.test(pathname)) return 'lesson-player';
  if (/^\/automation\/rules\/[^/]+\/?$/.test(pathname)) return 'automation-rule-builder';
  if (/^\/my-work(\/|$)/.test(pathname)) return 'my-work';
  if (/^\/projects\/[^/]+\/list\/?$/.test(pathname)) return 'project-list';
  if (/^\/projects\/[^/]+\/board\/?$/.test(pathname)) return 'project-board';
  if (/^\/admin(\/|$)/.test(pathname)) return 'admin';
  // Облік часу: тур трекера (перший крок — розділи модуля, тож він корисний на будь-якій вкладці).
  if (/^\/time(\/|$)/.test(pathname)) return 'time-tracker';
  if (/^\/legal\/matters\/[^/]+\/?$/.test(pathname)) return 'legal-matter';
  if (/^\/mgmt\/units\/?$/.test(pathname)) return 'mgmt-tree';
  // Хвиля 2В: картка зустрічі, перелік зустрічей, профіль працівника, завершення співпраці, Google, майстер перенесення.
  if (/^\/meetings\/?$/.test(pathname)) return 'meetings-list';
  if (/^\/meetings\/(?!settings\/?$|deletions\/?$|guest\/)[^/]+\/?$/.test(pathname)) return 'meeting-card';
  if (/^\/people\/offboarding\/[^/]+\/?$/.test(pathname)) return 'people-offboarding';
  if (/^\/people\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?$/i.test(pathname)) return 'people-profile';
  if (/^\/connectors\/google(\/|$)/.test(pathname)) return 'connectors-google';
  if (/^\/migration\/(new|jobs)\/[^/]+\/?$/.test(pathname)) return 'migration-wizard';
  // Хвиля 2Г: фінанси замовлення й конструктор звітів.
  if (/^\/finance\/matters\/[^/]+\/?$/.test(pathname)) return 'finance-matter';
  if (/^\/reports\/builder\/?$/.test(pathname)) return 'reports-builder';
  return null;
}

/**
 * «Екран» для тура: проєкт (усі його подання), розділ адміністрування, решта — перший сегмент адреси.
 * Перехід на інший екран завершує тур; перемикання подань проєкту чи підрозділів — ні.
 */
export function screenKey(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'projects' && parts[1]) return `/projects/${parts[1]}`;
  return `/${parts[0] ?? ''}`;
}

/** Кроки з довідки сервера чи іншого джерела: лише повні, з унікальними ідентифікаторами. */
export function sanitizeSteps(steps: readonly Partial<TourStep>[]): TourStep[] {
  const seen = new Set<string>();
  const out: TourStep[] = [];
  steps.forEach((s, i) => {
    const target = typeof s.target === 'string' ? s.target.trim() : '';
    const title = typeof s.title === 'string' ? s.title.trim() : '';
    const body = typeof s.body === 'string' ? s.body.trim() : '';
    if (!target || (!title && !body)) return;
    let id = typeof s.id === 'string' && s.id ? s.id : `step-${i + 1}`;
    if (seen.has(id)) id = `${id}-${i + 1}`;
    seen.add(id);
    const placement = s.placement && ['auto', 'top', 'bottom', 'left', 'right'].includes(s.placement) ? s.placement : undefined;
    out.push({ id, target, title: title || 'Крок', body, placement });
  });
  return out;
}
